/* eslint-disable no-unused-vars */
import dayjs, { Dayjs } from 'dayjs'
import updateLocale from 'dayjs/plugin/updateLocale'
import React, { useEffect, useMemo, useState } from 'react'
import { CALENDAR_TYPE, LEARNING_MODE } from '../../constants'
import { useEffectAfterMounted } from '../../hooks/useEffectAfterMounted'
import { ICourse, IEvent, IFilter, INorm } from '../../types'
import { splitEventsIntoDays } from '../../utils'
import CalendarLegend from './CalendarLegend/CalendarLegend'
import FilterCalendar from './FilterCalendar/FilterCalendar'
import HeaderCalendar from './HeaderCalendar/HeaderCalendar'
import LMSHeaderCalendar from './HeaderCalendar/LMSHeaderCalendar'
import LMSModeWeeklyCalendar from './LMSWeeklyCalendar'
import MonthlyCalendar from './MonthlyCalendar/MonthlyCalendar'
import styles from './SAPPCalendar.module.css'
import WeeklyCalendar from './WeeklyCalendar/WeeklyCalendar'

dayjs.extend(updateLocale)
dayjs.updateLocale('en', {
  weekStart: 1
})

interface IAntdCalendarProps {
  events: IEvent[]
  norms?: INorm[]
  teacherName?: string
  showTeacherName?: boolean
  showWeeklyNorm?: boolean
  onOpenDetail?: (date: Date, events: IEvent[]) => void
  onOpenCreate?: (date: Date) => void
  onRefetchAPI: (startDate: Date, endDate: Date) => Promise<void>
  onEventDetail?: (event: IEvent) => void
  loading?: boolean
  type?: string
  courses?: ICourse[]
  onfilter?: (filter: IFilter) => void
  hasFilter?: boolean
  headerType?: string
  isFiltered?: boolean
  fetchGetSchedule?: (params?: Object) => void
}

const SAPPCalendar = ({
  teacherName,
  events,
  norms = [],
  showTeacherName = true,
  showWeeklyNorm = true,
  onOpenDetail,
  onOpenCreate,
  onRefetchAPI,
  onEventDetail,
  loading,
  type = 'OPS',
  courses,
  onfilter,
  hasFilter = false,
  headerType = CALENDAR_TYPE.OPS,
  isFiltered = false,
  fetchGetSchedule
}: IAntdCalendarProps) => {
  const [currentDate, setCurrentDate] = useState<Dayjs>(dayjs())
  const [calendarMode, setCalendarMode] = useState<any>('month')
  const [filter, setFilter] = useState<IFilter>({
    courseId: courses ? courses.map((item) => item.id) : [],
    isOpen: false,
    type: Object.values(LEARNING_MODE),
    day: ''
  })
  const startDate = useMemo(
    () => currentDate.startOf(calendarMode).startOf('week'),
    [currentDate, calendarMode]
  )

  const endDate = useMemo(
    () => currentDate.endOf(calendarMode).endOf('week'),
    [currentDate, calendarMode]
  )

  const splittedEvents = splitEventsIntoDays(events)
  splittedEvents.sort((a, b) => dayjs(a.startDate).diff(b.startDate))

  const handleFilterDate = (date: Dayjs) => {
    setCurrentDate(date)
  }

  useEffect(() => {
    onfilter && onfilter(filter)
  }, [filter])

  useEffectAfterMounted(() => {
    onRefetchAPI(startDate.toDate(), endDate.toDate())
  }, [startDate, endDate])

  useEffect(() => {
    setFilter((prev) => ({
      ...prev,
      courseId: courses ? courses.map((item) => item.id) : []
    }))
  }, [courses])

  return (
    <div className={styles['sapp-calendar']}>
      {filter.isOpen && (
        <FilterCalendar
          filter={filter}
          courses={courses}
          setFilter={setFilter}
          onRefetchAPI={(date) => handleFilterDate(dayjs(date))}
        />
      )}
      <div className={styles['sapp-calendar__container']}>
        {headerType === CALENDAR_TYPE.LMS ? (
          <LMSHeaderCalendar
            title={teacherName}
            showTeacherName={showTeacherName}
            currentDate={currentDate}
            setCurrentDate={setCurrentDate}
            calendarMode={calendarMode}
            setCalendarMode={setCalendarMode}
            toggleTurnOnFilter={() =>
              setFilter((prev) => ({ ...prev, isOpen: !filter.isOpen }))
            }
            hasFilter={hasFilter}
            filter={filter}
          />
        ) : (
          <HeaderCalendar
            title={teacherName}
            showTeacherName={showTeacherName}
            currentDate={currentDate}
            setCurrentDate={setCurrentDate}
            calendarMode={calendarMode}
            setCalendarMode={setCalendarMode}
            isFiltered={isFiltered}
            fetchGetSchedule={fetchGetSchedule}
            loading={loading}
          />
        )}

        {headerType === CALENDAR_TYPE.OPS && <CalendarLegend />}

        {calendarMode === 'month' && (
          <MonthlyCalendar
            currentDate={currentDate}
            startDate={startDate}
            endDate={endDate}
            events={splittedEvents}
            norms={norms}
            showWeeklyNorm={showWeeklyNorm}
            onOpenDetail={onOpenDetail}
            onOpenCreate={onOpenCreate}
            onEventDetail={onEventDetail}
            loading={loading}
          />
        )}
        {calendarMode === 'week' && type === CALENDAR_TYPE.LMS && (
          <LMSModeWeeklyCalendar
            startDate={startDate}
            endDate={endDate}
            events={splittedEvents}
            norms={norms}
            showWeeklyNorm={showWeeklyNorm}
            onOpenDetail={onOpenDetail}
            onOpenCreate={onOpenCreate}
            onEventDetail={onEventDetail}
            loading={loading}
          />
        )}
        {calendarMode === 'week' && type === CALENDAR_TYPE.OPS && (
          <WeeklyCalendar
            startDate={startDate}
            endDate={endDate}
            events={splittedEvents}
            norms={norms}
            showWeeklyNorm={showWeeklyNorm}
            onOpenDetail={onOpenDetail}
            onOpenCreate={onOpenCreate}
            onEventDetail={onEventDetail}
            loading={loading}
          />
        )}
      </div>
    </div>
  )
}

export default SAPPCalendar
